import { apiSlice } from "../../../features/api/apiSlice";

/**
 * A chapter's own CPD events — both ends of the same flow in one file.
 *
 * The chairman's endpoints live under /chapter/event-requests and the
 * secretariat's under /admin/chapter-event-requests, and the screens for them sit
 * in different modules (chapter / events). They are kept together here anyway
 * because they are one feature with one set of statuses: the tags below are what
 * make an approval on the admin screen show up on the chairman's, and splitting
 * the file would mean two copies of the status vocabulary that have to agree.
 */

export type ChapterEventStatus =
  | "pending"
  /**
   * The reviewer has asked the chapter something and is waiting on the answer.
   * Not a refusal: the request is back with the chairman, still editable, and
   * returns to `pending` when he responds.
   */
  | "queried"
  | "approved"
  | "rejected"
  | "attendance_submitted"
  | "allocated";

/** Which side a request is sitting with — what the queues on both screens sort by. */
export type ChapterEventAwaiting = "reviewer" | "chapter" | null;

/**
 * One entry on the review trail.
 *
 * Shared, append-only, and the SAME rows on both screens — the chairman reads
 * everything the reviewer writes and the other way round, which is the point of
 * it. Formatted by the API so neither side can render the same exchange
 * differently.
 */
export type ChapterEventReview = {
  id: number;
  /**
   * raised · amended · queried · responded · commented · approved · rejected ·
   * attendance_submitted · allocated. Left as a string rather than a union: the
   * vocabulary grows with the flow, and an unknown action should render as
   * itself rather than breaking the trail.
   */
  action: string;
  from_status: string | null;
  to_status: string | null;
  message: string | null;
  /** The points as they stood when this was written, where the entry is about them. */
  points: number | null;
  actor_role: "chapter" | "reviewer" | "system";
  /** Null on a system entry, or where the account has since been deleted. */
  actor: string | null;
  created_at: string | null;
};

// Their wording lives in ChapterEventStatusBadge, which is the one place a
// status is turned into words — a second map here is how the chairman's screen
// and the secretariat's end up describing the same row differently.

/** One row of the resource-persons table under a day's technical papers. */
export type ChapterEventResourcePerson = {
  /** The sub-theme or technical paper this person is down for. */
  topic: string;
  person: string;
  profile: string;
};

export type ChapterEventAppendageDay = {
  /** Y-m-d. Derived by the API from the event's own dates — never typed. */
  date: string;
  /** A brief summary of that day's programme. */
  programme: string;
  /** The themes, subthemes and training sessions planned for it. */
  sessions: string;
  technical_papers: string;
  resource_persons: ChapterEventResourcePerson[];
};

/**
 * The appendage to the request for CPD allocation — the Institute's template.
 *
 * Always arrives whole, with one `days` entry per day of the event, even on a
 * request nobody has typed a word into. The form renders what it is given
 * rather than working out for itself how many days it should be offering, so
 * extending an event by a day is what adds a day to the appendage.
 */
export type ChapterEventAppendage = {
  introduction: string;
  aims_objectives: string;
  about_theme: string;
  days: ChapterEventAppendageDay[];
  other_information: string;
};

export type ChapterEventAttendee = {
  id: number;
  user_id: number | null;
  membership_no: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  /** False for a name the member register does not recognise — recorded, credited nothing. */
  is_member: boolean;
  source: "manual" | "import";
  cpd_points: number | null;
  cpd_issued: boolean;
  issued_at: string | null;
  registration_id?: number | null;
  /**
   * Whether this row may still be corrected. Not a property of the request's
   * stage but of whether it has been paid out: an uncredited row stays editable
   * even after the CPD has been allocated, a credited one never is.
   */
  can_edit: boolean;
  /** Matched no member, so it earns nothing until somebody fixes it. */
  needs_match: boolean;
};

export type ChapterEventAttendeePatch = {
  name?: string;
  email?: string | null;
  phone?: string | null;
  membership_no?: string | null;
  /** From the member search — surer than any number the chairman could type. Null detaches. */
  user_id?: number | null;
};

export type ChapterEventRequest = {
  id: string;
  chapter: { id: string; name: string } | null;
  title: string;
  theme: string | null;
  details?: string | null;
  location: string;
  start_date: string;
  end_date: string;
  proposed_cpd_points: number;
  approved_cpd_points: number | null;
  /** What actually applies — the approved figure where there is one, else the ask. */
  effective_cpd_points: number;
  /** True when the secretariat allowed fewer points than were asked for. */
  cpd_was_reduced: boolean;
  status: ChapterEventStatus;
  review_notes: string | null;
  requested_by?: string | null;
  requested_by_email?: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  attendance_submitted_at: string | null;
  allocated_by?: string | null;
  allocated_at: string | null;
  event_id: string | null;
  attendees_count: number;
  member_attendees_count: number;
  /** Rows nobody was matched to — recorded, earning nothing, still correctable. */
  unmatched_count: number;
  /** Matched but not yet paid out: what a press of Allocate would credit. */
  uncredited_member_count: number;
  has_unmatched?: boolean;
  created_at: string | null;
  /** True once the appendage carries enough to be reviewed. A flag, not a gate. */
  appendage_complete?: boolean;
  appendage_updated_at?: string | null;
  /** Whose move it is. Derived from the status by the API, not re-derived here. */
  awaiting?: ChapterEventAwaiting;
  /**
   * Entries on the review trail THIS reader has not seen — written by the other
   * side, after their last visit. Per person, not per side: one reviewer opening
   * a request does not mark it read for the rest of the desk.
   */
  unread_count?: number;
  can_edit?: boolean;
  /** Follows the request itself — writable while pending, queried or rejected. */
  can_edit_appendage?: boolean;
  /** Chairman only: answering a query is what hands the request back. */
  can_respond?: boolean;
  /** Either side may add to the trail until the CPD has been allocated. */
  can_comment?: boolean;
  /** Reviewer only: ask the chapter something without deciding anything. */
  can_query?: boolean;
  can_upload_attendance?: boolean;
  can_submit_attendance?: boolean;
  /**
   * Allocation is re-runnable, so this is not a question of stage alone: an
   * already-allocated request offers it again as soon as a previously unmatched
   * row has been matched.
   */
  can_review?: boolean;
  can_allocate?: boolean;
};

export type ChapterEventRequestDetail = ChapterEventRequest & {
  details: string | null;
  appendage: ChapterEventAppendage;
  /** Oldest first — it is read as a conversation, not as a feed. */
  reviews: ChapterEventReview[];
  /**
   * Where this reader had got to WHEN THEY OPENED IT. Everything on the trail
   * after this was new on arrival; null means they had never opened it, so all
   * of it was.
   *
   * Opening the request moves the mark server-side, so this value is already
   * stale by the time it arrives — which is the point. The screen freezes it on
   * first load so the highlights survive the refetches that follow every post;
   * next visit, the same entries are read.
   */
  unread_since?: string | null;
  attendees: ChapterEventAttendee[];
  allocation: {
    event_id: string | null;
    event_title: string | null;
    points_each: number;
    credited: number;
    not_credited: number;
    total_points: number;
    allocated_at?: string | null;
    allocated_by?: string | null;
  } | null;
};

export type ChapterEventRequestPayload = {
  title: string;
  theme?: string;
  details?: string;
  location: string;
  start_date: string;
  end_date: string;
  proposed_cpd_points: number | string;
  /**
   * Saved through the same endpoint as the rest of the request, on its own:
   * `updateChapterEventRequest({ id, body: { appendage } })`. It is part of the
   * request, so saving it has to mean what saving the request means — on a
   * rejected one, that it goes back into the secretariat's queue.
   */
  appendage?: ChapterEventAppendage;
  /** Only sent by a chairman acting for a second chapter. */
  chapter_id?: string;
};

export type ChapterEventListResponse = {
  chapter: any;
  chapter_id: string;
  chapter_ids?: string[];
  acting_for?: { id: string; name: string }[];
  data: ChapterEventRequest[];
};

export type AdminChapterEventListResponse = {
  data: ChapterEventRequest[];
  meta: { counts: Record<ChapterEventStatus, number> };
};

/** One row of an attendance upload's verdict. */
export type AttendanceImportRow = {
  line: number;
  name: string;
  email: string | null;
  membership_no: string | null;
  type: "member" | "unmatched";
  /**
   * `warning` is a row that IMPORTS but matched no member — typically a mistyped
   * number. It is not an error, because rejecting it would drop the person from
   * the attendance silently; it lands flagged and is corrected afterwards.
   */
  status: "ok" | "warning" | "error";
  errors: string[];
  warnings: string[];
};

export type AttendanceImportResult = {
  dry_run: boolean;
  total_rows: number;
  valid: number;
  invalid: number;
  members: number;
  /** Rows that will be recorded without CPD — no number, or one that matched nobody. */
  unmatched: number;
  /** Of those, the ones carrying a number that did not resolve. */
  flagged: number;
  imported: number;
  rows: AttendanceImportRow[];
};

const LIST = { type: "ChapterEventRequests" as const, id: "LIST" };
const ADMIN_LIST = { type: "ChapterEventRequests" as const, id: "ADMIN_LIST" };

export const chapterEventsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    /* ───────────── Chairman ───────────── */

    getChapterEventRequests: b.query<ChapterEventListResponse, { status?: string } | void>({
      query: (params) => ({ url: "/chapter/event-requests", params: params ?? {} }),
      providesTags: [LIST],
    }),

    getChapterEventRequest: b.query<ChapterEventRequestDetail, string>({
      query: (id) => `/chapter/event-requests/${id}`,
      transformResponse: (r: any) => r?.data ?? r,
      providesTags: (_r, _e, id) => [{ type: "ChapterEventRequests" as const, id }],
    }),

    createChapterEventRequest: b.mutation<any, ChapterEventRequestPayload>({
      query: (body) => ({ url: "/chapter/event-requests", method: "POST", body }),
      invalidatesTags: [LIST, ADMIN_LIST],
    }),

    // POST, not PUT — the host blocks PATCH and the API takes POST for updates.
    updateChapterEventRequest: b.mutation<any, { id: string; body: Partial<ChapterEventRequestPayload> }>({
      query: ({ id, body }) => ({ url: `/chapter/event-requests/${id}`, method: "POST", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        LIST,
        ADMIN_LIST,
      ],
    }),

    deleteChapterEventRequest: b.mutation<any, string>({
      query: (id) => ({ url: `/chapter/event-requests/${id}`, method: "DELETE" }),
      invalidatesTags: [LIST, ADMIN_LIST],
    }),

    /**
     * The chairman's answer to a query, which hands the request back.
     *
     * Distinct from saving an amendment: a queried request stays with the
     * chapter however often he edits it, and THIS is the one act that returns
     * it to the reviewer. Invalidates the admin list because the request
     * reappears in the reviewer's queue.
     */
    respondToChapterEventQuery: b.mutation<any, { id: string; message: string }>({
      query: ({ id, message }) => ({
        url: `/chapter/event-requests/${id}/respond`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        LIST,
        ADMIN_LIST,
      ],
    }),

    /** A note on the trail that decides nothing. The reviewer reads it. */
    commentOnChapterEventRequest: b.mutation<any, { id: string; message: string }>({
      query: ({ id, message }) => ({
        url: `/chapter/event-requests/${id}/comment`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "ChapterEventRequests" as const, id }],
    }),

    addChapterEventAttendee: b.mutation<
      any,
      { id: string; body: { user_id?: number; membership_no?: string; name?: string; email?: string; phone?: string } }
    >({
      query: ({ id, body }) => ({
        url: `/chapter/event-requests/${id}/attendees`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "ChapterEventRequests" as const, id }, LIST],
    }),

    /**
     * Correct one row — name, email, phone or membership number.
     *
     * Changing the number re-runs the match, which is how an unmatched attendee
     * becomes creditable. Invalidates the ADMIN list too: a row going from
     * unmatched to matched is what makes the Allocate button reappear on the
     * secretariat's side.
     */
    updateChapterEventAttendee: b.mutation<
      any,
      { id: string; attendeeId: number; body: ChapterEventAttendeePatch }
    >({
      query: ({ id, attendeeId, body }) => ({
        url: `/chapter/event-requests/${id}/attendees/${attendeeId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        LIST,
        ADMIN_LIST,
      ],
    }),

    removeChapterEventAttendee: b.mutation<any, { id: string; attendeeId: number }>({
      query: ({ id, attendeeId }) => ({
        url: `/chapter/event-requests/${id}/attendees/${attendeeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        LIST,
        ADMIN_LIST,
      ],
    }),

    /**
     * Dry run of the attendance upload.
     *
     * Writes nothing — it parses the sheet and hands back a verdict per row, so
     * the chairman sees who will be added, and who on it is not a member and will
     * therefore earn nothing, before he commits. Deliberately NOT
     * cache-invalidating: nothing on the server changed.
     */
    previewChapterEventAttendance: b.mutation<any, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/chapter/event-requests/${id}/attendees/import/preview`,
        method: "POST",
        body,
      }),
    }),

    importChapterEventAttendance: b.mutation<any, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/chapter/event-requests/${id}/attendees/import`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "ChapterEventRequests" as const, id }, LIST],
    }),

    submitChapterEventAttendance: b.mutation<any, string>({
      query: (id) => ({
        url: `/chapter/event-requests/${id}/submit-attendance`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ChapterEventRequests" as const, id },
        LIST,
        // It is now in the secretariat's tray.
        ADMIN_LIST,
      ],
    }),

    /* ───────────── Secretariat ───────────── */

    adminGetChapterEventRequests: b.query<
      AdminChapterEventListResponse,
      { status?: string; chapter_id?: string; search?: string } | void
    >({
      query: (params) => ({ url: "/admin/chapter-event-requests", params: params ?? {} }),
      providesTags: [ADMIN_LIST],
    }),

    adminGetChapterEventRequest: b.query<ChapterEventRequestDetail, string>({
      query: (id) => `/admin/chapter-event-requests/${id}`,
      transformResponse: (r: any) => r?.data ?? r,
      providesTags: (_r, _e, id) => [{ type: "ChapterEventRequests" as const, id }],
    }),

    adminApproveChapterEventRequest: b.mutation<
      any,
      { id: string; approved_cpd_points?: number | string | null; notes?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/chapter-event-requests/${id}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        ADMIN_LIST,
        LIST,
      ],
    }),

    /**
     * Ask the chapter for something, without deciding anything.
     *
     * The third thing a reviewer can do with a pending request, and the reason
     * the trail exists — before it, a reviewer who wanted a fuller appendage
     * had to reject the whole request to say so. Invalidates the chapter's list
     * too: the request lands in the chairman's queue as his move.
     */
    adminQueryChapterEventRequest: b.mutation<any, { id: string; message: string }>({
      query: ({ id, message }) => ({
        url: `/admin/chapter-event-requests/${id}/query`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        ADMIN_LIST,
        LIST,
      ],
    }),

    /** A note on the trail that decides nothing. The chapter reads it. */
    adminCommentOnChapterEventRequest: b.mutation<any, { id: string; message: string }>({
      query: ({ id, message }) => ({
        url: `/admin/chapter-event-requests/${id}/comment`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "ChapterEventRequests" as const, id }],
    }),

    adminRejectChapterEventRequest: b.mutation<any, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/admin/chapter-event-requests/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterEventRequests" as const, id },
        ADMIN_LIST,
        LIST,
      ],
    }),

    /**
     * The one press that creates the chapter event, records its attendance and
     * issues the CPD.
     *
     * So it moves far more than this screen: a new event, a registration per
     * attendee, and a ledger entry per member. Everything that reads those is
     * invalidated, which is why this mutation's tag list is so much longer than
     * the two above it.
     */
    adminAllocateChapterEventCpd: b.mutation<any, string>({
      query: (id) => ({
        url: `/admin/chapter-event-requests/${id}/allocate-cpd`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ChapterEventRequests" as const, id },
        ADMIN_LIST,
        LIST,
        "AdminEvents" as const,
        "Events" as const,
        "AdminCpd" as const,
        "CpdLedger" as const,
        "CpdSummary" as const,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetChapterEventRequestsQuery,
  useGetChapterEventRequestQuery,
  useCreateChapterEventRequestMutation,
  useUpdateChapterEventRequestMutation,
  useDeleteChapterEventRequestMutation,
  useRespondToChapterEventQueryMutation,
  useCommentOnChapterEventRequestMutation,
  useAddChapterEventAttendeeMutation,
  useUpdateChapterEventAttendeeMutation,
  useRemoveChapterEventAttendeeMutation,
  usePreviewChapterEventAttendanceMutation,
  useImportChapterEventAttendanceMutation,
  useSubmitChapterEventAttendanceMutation,

  useAdminGetChapterEventRequestsQuery,
  useAdminGetChapterEventRequestQuery,
  useAdminApproveChapterEventRequestMutation,
  useAdminQueryChapterEventRequestMutation,
  useAdminCommentOnChapterEventRequestMutation,
  useAdminRejectChapterEventRequestMutation,
  useAdminAllocateChapterEventCpdMutation,
} = chapterEventsApi;
